// hooks/useProductsLogic.js - Enhanced with variant discount support
import { useMemo } from 'react';
import { getLocalizedProductName } from '../../../../../utils/multilingualUtils';

export const useProductsLogic = ({
    products,
    salesItems,
    search,
    categoryFilter,
    statusFilter,
    selectedProducts,
    setSelectedProducts
}) => {
    // console.log("🔧 useProductsLogic - salesItems received:", salesItems);

    // Enhanced function to check if product has an active discount
    // Now supports both product-level and variant-level discounts
    const getProductDiscount = (productId, variantId) => {
        const discount = salesItems.find(sale => {
            const matchesProduct = sale.productId === productId;

            // Fix the variant matching logic
            let matchesVariant;
            if (variantId !== undefined && variantId !== null) {
                // Looking for a specific variant
                matchesVariant = sale.variantId === variantId;
            } else {
                // Looking for product-level discount (no variant)
                matchesVariant = !sale.variantId || sale.variantId === null || sale.variantId === undefined;
            }

            const isActive = sale.isActive;
            const notExpired = !sale.endDate || new Date(sale.endDate) > new Date();

            return matchesProduct && matchesVariant && isActive && notExpired;
        });

        return discount;
    };    // Get all discounts for a product (including variant discounts)
    const getProductDiscounts = (productId) => {
        const discounts = salesItems.filter(sale => {
            const matchesProduct = sale.productId === productId;
            const isActive = sale.isActive;
            const notExpired = !sale.endDate || new Date(sale.endDate) > new Date();

            return matchesProduct && isActive && notExpired;
        });

        return discounts;
    };

    // Enhanced calculate discounted price function
    const calculateDiscountedPrice = (originalPrice, discount) => {
        if (!discount) return originalPrice;

        // Handle different property names from different sources
        const discountType = discount.discountType || discount.type;
        const discountValue = discount.discountValue || discount.value;

        // Ensure we have valid numbers
        const price = parseFloat(originalPrice) || 0;
        const value = parseFloat(discountValue) || 0;

        if (discountType === "percentage") {
            return price * (1 - value / 100);
        } else {
            return Math.max(0, price - value);
        }
    };

    // Filter products based on search, category, and status
    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            // Get the product name in the current language for searching
            const productName = getLocalizedProductName(product, 'en') || '';
            const matchesSearch = productName.toLowerCase().includes(search.toLowerCase());
            const matchesCategory = categoryFilter === "All" || product.category === categoryFilter;
            const matchesStatus = statusFilter === "All" || product.status === statusFilter;

            return matchesSearch && matchesCategory && matchesStatus;
        });
    }, [products, search, categoryFilter, statusFilter]);

    // Handle bulk selection
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedProducts(filteredProducts.map(product => product.id));
        } else {
            setSelectedProducts([]);
        }
    };

    // Handle individual selection
    const handleSelectProduct = (productId) => {
        if (selectedProducts.includes(productId)) {
            setSelectedProducts(selectedProducts.filter(id => id !== productId));
        } else {
            setSelectedProducts([...selectedProducts, productId]);
        }
    };

    // Check if product has any active discounts (product-level or variant-level)
    const hasAnyDiscount = (productId) => {
        const hasDiscount = getProductDiscounts(productId).length > 0;
        // console.log(`🎯 hasAnyDiscount(${productId}):`, hasDiscount);
        return hasDiscount;
    };

    // Get the primary discount to display (prefer product-level over variant-level)
    const getPrimaryDiscount = (productId) => {
        const discounts = getProductDiscounts(productId);

        // Prefer product-level discount (no variantId)
        const productDiscount = discounts.find(d => !d.variantId);
        if (productDiscount) {
            // console.log(`🎯 getPrimaryDiscount(${productId}) - product level:`, productDiscount);
            return productDiscount;
        }

        // Fall back to first variant discount
        const variantDiscount = discounts[0] || null;
        // console.log(`🎯 getPrimaryDiscount(${productId}) - variant level:`, variantDiscount);
        return variantDiscount;
    };

    return {
        filteredProducts,
        handleSelectAll,
        handleSelectProduct,
        getProductDiscount,
        getProductDiscounts,
        calculateDiscountedPrice,
        hasAnyDiscount,
        getPrimaryDiscount
    };
};